import { useMemo } from 'react';
import { toISODate } from '../lib/format';

export type PresetKey =
  | 'today'
  | 'yesterday'
  | 'week'
  | 'month'
  | 'custom';

export interface DateRange {
  preset: PresetKey;
  from: string; // YYYY-MM-DD
  to: string; // YYYY-MM-DD
}

const PRESETS: { key: PresetKey; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'custom', label: 'Custom' },
];

export function computeRange(preset: PresetKey, base = new Date()): DateRange {
  const today = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  let from = today;
  let to = today;

  switch (preset) {
    case 'today':
      break;
    case 'yesterday': {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      from = y;
      to = y;
      break;
    }
    case 'week': {
      // Monday-based week.
      const day = (today.getDay() + 6) % 7;
      const monday = new Date(today);
      monday.setDate(today.getDate() - day);
      from = monday;
      to = today;
      break;
    }
    case 'month': {
      from = new Date(today.getFullYear(), today.getMonth(), 1);
      to = today;
      break;
    }
    case 'custom':
      break;
  }

  return { preset, from: toISODate(from), to: toISODate(to) };
}

export function DateRangePicker({
  value,
  onChange,
}: {
  value: DateRange;
  onChange: (r: DateRange) => void;
}) {
  const maxDate = useMemo(() => toISODate(new Date()), []);

  function selectPreset(key: PresetKey) {
    if (key === 'custom') {
      onChange({ ...value, preset: 'custom' });
    } else {
      onChange(computeRange(key));
    }
  }

  return (
    <div className="row gap-sm flex-wrap">
      <div className="pill-tabs">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            className={value.preset === p.key ? 'active' : ''}
            onClick={() => selectPreset(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>
      {value.preset === 'custom' && (
        <div className="row gap-sm">
          <input
            type="date"
            style={{ width: 'auto' }}
            value={value.from}
            max={value.to || maxDate}
            onChange={(e) => onChange({ ...value, from: e.target.value })}
          />
          <span className="text-muted">to</span>
          <input
            type="date"
            style={{ width: 'auto' }}
            value={value.to}
            min={value.from}
            max={maxDate}
            onChange={(e) => onChange({ ...value, to: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}
